import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";

/**
 * The moving half of a barrier — what actually turns on the bearing. Matches
 * BarrierConfig#head, so these numbers are part of the level format and must
 * not be reordered.
 */
export enum BarrierHead
{
  /** Two opposite blades across the hub: a propeller, spinning continuously. */
  Blade = 0,
  /** A hammer on an arm, swinging to and fro like a pendulum. */
  Hammer = 1,
}

/**
 * What holds the bearing up. Matches BarrierConfig#mount — same warning about
 * the numbers. 0 was a post standing on the ground; it is retired and must not
 * be reused.
 */
export enum BarrierMount
{
  /** A pillar dropping out of the sky, bearing at its bottom end. */
  Ceiling = 1,
  /** A rod reaching in from the left edge of the frame, bearing at its end. */
  Left = 2,
  /** A rod reaching in from the right edge of the frame, bearing at its end. */
  Right = 3,
}

/** One piece of art bolted into the barrier, placed relative to the bearing. */
interface BarrierPart
{
  model: ResourceID;
  /** Where the part's own origin goes, in WORLD units, before `angle`. */
  offset: THREE.Vector3;
  /** Turn about the depth axis, radians. The art is authored in the XY plane. */
  angle: number;
  /** Visual depth only: 0 = beam, 1 = striking part, 2 = bearing cap. */
  visual_layer: number;
  /** Stretches the part along its own length (+X, before `angle`). */
  stretch?: number;
  /** Uniform size multiplier on top of the shared model scale. */
  size?: number;
  /** Extra multiplier across the part (Y and Z, before `angle`) — a rod's girth. */
  thickness?: number;
}

interface BarrierPartDebug
{
  group: "mount" | "head";
  model: string;
  angle_deg: number;
  stretch: number;
  size: number;
  thickness: number;
  layer: number;
  visual_z: number;
  offset_x: number;
  offset_y: number;
  size_x: number;
  size_y: number;
  size_z: number;
  collider_x: number;
  collider_y: number;
  collider_z: number;
  collider_depth: number;
}

/**
 * An obstacle standing in front of the table — a propeller or a swinging
 * hammer the ball has to get past on its way to the blocks.
 *
 * It is indestructible and deliberately NOT a LevelEntity: GameScreen ends the
 * level once every entity is gone (see checkGameEnd), so a barrier in the
 * entity list would keep a cleared level running forever. It lives in
 * GameScreen#barriers instead, and never counts as a score target.
 *
 * Two rigid bodies, because half of a barrier holds still and half turns:
 *   MOUNT (fixed)      the bearing and, for a propeller, its support pillar.
 *   HEAD  (kinematic)  the two blades, or the hammer on its arm, turning
 *                      about the bearing.
 *
 * The art (assets/models/barriers) is authored Z-up in centimetre-ish units,
 * which the FBX import turns into a -90° X rotation on the mesh. Every part is
 * an open-backed shell whose face ends up pointing away from the camera, so
 * addPart turns each one round about its own long axis first. Measured in
 * world axes afterwards the parts read as:
 *   bearing — a disc in the XY plane, ~156 across, origin at its centre
 *   blade   — ONE blade pointing +Y, reaching from y≈62 out to y≈419 (the
 *             propeller in the reference art is two of them, 180° apart)
 *   pillar  — a rod along +X, 0..373, origin at the near end
 *   hammer  — a head across X, ~464 long, origin at its centre
 * Both groups are composed around the bearing at the origin, so the head moves
 * by simply turning about Z.
 */
export class Barrier
{
  /**
   * Length of the pillar rod as authored, in grid cells. The single knob for
   * how big the set is: every part is scaled by the factor derived from it, so
   * the pieces stay in proportion. At 2 cells the propeller spans ~4.5 cells
   * and the hammer hangs ~3 cells from its bearing.
   */
  private static readonly PILLAR_LENGTH_CELLS = 3;

  // Per-part sizes, as multipliers of the proportions the art has at
  // PILLAR_LENGTH_CELLS — 1 keeps a part as authored, 1.5 makes it half as big
  // again. Every part's collider is measured from its scaled art, so physics
  // follows these on its own.
  /** The round cap at the pivot. */
  private static readonly BEARING_SIZE = 0.75;
  /** Each propeller blade — its length and its width together. */
  private static readonly BLADE_SIZE = 1;
  /** The hammer's striking head. */
  private static readonly HAMMER_HEAD_SIZE = 1;
  /** How long the hammer's arm is; the head always sits on its far end. */
  private static readonly HAMMER_ARM_LENGTH = 1;
  /** How thick the hammer's arm is, without changing its length. */
  private static readonly HAMMER_ARM_THICKNESS = 1;
  /** How long ONE tile of the mount rod is; the tiles still run off-frame. */
  private static readonly MOUNT_ROD_TILE_LENGTH = 1;
  /** How thick the mount rod is, without changing its length. */
  private static readonly MOUNT_ROD_THICKNESS = 0.75;
  /** Depth step between the art layers, and the minimum collider depth. */
  // Must exceed half the combined depth of two neighbouring FBX parts. A
  // smaller value still let their curved surfaces intersect on screen.
  private static readonly VISUAL_LAYER_DEPTH_CELLS = 0.4;

  private static readonly BLADE_COUNT = 2;
  /** Full turns per second of a Blade head. Negative spins it the other way. */
  private static readonly BLADE_TURNS_PER_SEC = 0.35;
  /** Half-width of the hammer's swing, radians. */
  private static readonly HAMMER_SWING_AMPLITUDE = Math.PI / 3;
  /** Seconds for one there-and-back swing. */
  private static readonly HAMMER_SWING_PERIOD = 2.4;

  // A barrier is meant to send the ball somewhere else, so it gets its own
  // surface rather than borrowing a block's. Metal-block grip (0.7) let the
  // ball settle into the fork between two blades and ride the propeller round
  // instead of glancing off it (confirmed by hand); this is slick and springy
  // enough to deflect. The liveliest pair of numbers in the file — turn the
  // bounce down first if barriers start throwing shots too far.
  private static readonly FRICTION = 0.25;
  private static readonly RESTITUTION = 0.3;

  private static material: THREE.MeshLambertMaterial | null = null;
  private static model_scale = 0;
  private static debug_id = 0;

  public readonly head: BarrierHead;
  public readonly mount: BarrierMount;
  /** Everything that holds still: the bearing and any support pillar. */
  public readonly mount_mesh: THREE.Object3D;
  /** Everything that turns: the blades, or the hammer on its arm. */
  public readonly head_mesh: THREE.Object3D;
  public readonly mount_body: RAPIER.RigidBody;
  public readonly head_body: RAPIER.RigidBody;

  private readonly rest_angle: number;
  private angle = 0;
  private time = 0;
  private disposed = false;

  /**
   * @param bearing       world position of the pivot: the bottom of a Ceiling
   *                      pillar, the inner end of a Left / Right rod.
   * @param mount_length  how long the static rod has to be to leave the frame —
   *                      up out of the top, or out past the side edge.
   * @param angle         extra turn about the depth axis, radians. Turns the
   *                      head only, so it picks a hammer's rest direction.
   */
  constructor(
    head: BarrierHead,
    mount: BarrierMount,
    bearing: THREE.Vector3,
    mount_length: number,
    angle = 0
  )
  {
    this.head = head;
    this.mount = mount;
    this.rest_angle = angle;
    this.angle = angle;

    this.mount_mesh = new THREE.Group();
    this.mount_mesh.position.copy( bearing );
    this.mount_mesh.renderOrder = 10;

    this.head_mesh = new THREE.Group();
    this.head_mesh.position.copy( bearing );
    this.head_mesh.rotation.z = angle;
    this.head_mesh.renderOrder = 10;

    const world = PhysicsWorld.getWorld();

    this.mount_body = world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation( bearing.x, bearing.y, bearing.z )
    );

    this.head_body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation( bearing.x, bearing.y, bearing.z )
        .setRotation( Barrier.quaternionAboutZ( angle ) )
    );

    const debug_parts: BarrierPartDebug[] = [];
    for ( const part of Barrier.getMountParts( mount, head, mount_length ) )
      this.mount_mesh.add( this.addPart( part, this.mount_body, "mount", debug_parts ) );

    for ( const part of Barrier.getHeadParts( head, mount ) )
      this.head_mesh.add( this.addPart( part, this.head_body, "head", debug_parts ) );

    Barrier.logAssembly( ++Barrier.debug_id, head, mount, bearing, mount_length, debug_parts );

    SceneManager.world.add( this.mount_mesh );
    SceneManager.world.add( this.head_mesh );
  }

  /** Length of the authored pillar rod in world units. */
  private static getPillarLength(): number
  {
    return PhysicsCube.getSize().y * Barrier.PILLAR_LENGTH_CELLS;
  }

  /**
   * World units per model unit, shared by every part so the set keeps its
   * authored proportions. Derived from the pillar because it is the one part
   * whose length reads as a plain number of grid cells.
   */
  private static getModelScale(): number
  {
    if ( Barrier.model_scale === 0 )
    {
      const pillar = ResourceManager.getModel( ResourceID.BarrierPillarModel );
      pillar.updateMatrixWorld( true );
      const length = new THREE.Box3().setFromObject( pillar ).getSize( new THREE.Vector3() ).x;

      if ( !( length > 0 ) )
        throw new Error( "Barrier pillar model has invalid dimensions" );

      Barrier.model_scale = Barrier.getPillarLength() / length;
    }

    return Barrier.model_scale;
  }

  /**
   * Turns a freshly loaded part to face the camera and scales it by its own
   * size knobs. Shared by addPart and measure, so a part is laid out from
   * exactly the dimensions it is then drawn with.
   */
  private static orientAndScale(
    visual: THREE.Object3D,
    part: Pick<BarrierPart, "model" | "size" | "stretch" | "thickness">
  ): void
  {
    if ( part.model === ResourceID.BarrierPillarModel )
      visual.rotation.x = Math.PI;
    else
      visual.rotation.y = Math.PI;

    // `size` grows the whole part from its own origin; a rod is authored along
    // +X, so `stretch` lengthens it without fattening it and `thickness`
    // fattens it without lengthening it.
    const scale = Barrier.getModelScale() * ( part.size ?? 1 );
    const girth = scale * ( part.thickness ?? 1 );
    visual.scale.set( scale * ( part.stretch ?? 1 ), girth, girth );
  }

  /**
   * A part's bounds at its own size, around its own origin and before its
   * `angle`. Parts are fitted to one another from these real dimensions rather
   * than from where the art happens to put them, so resizing one part moves
   * the parts attached to it instead of leaving them floating or buried.
   */
  private static measure( part: Pick<BarrierPart, "model" | "size" | "stretch" | "thickness"> ): THREE.Box3
  {
    const visual = ResourceManager.getModel( part.model );
    Barrier.orientAndScale( visual, part );
    visual.updateMatrixWorld( true );

    return new THREE.Box3().setFromObject( visual );
  }

  /** Radius of the bearing cap at a given size — the disc lies in the XY plane. */
  private static bearingRadius( size: number ): number
  {
    return Barrier.measure( { model: ResourceID.BarrierBearingModel, size } ).getSize( new THREE.Vector3() ).x * 0.5;
  }

  private static headAngle( mount: BarrierMount ): number
  {
    switch ( mount )
    {
      case BarrierMount.Left: return 0;
      case BarrierMount.Right: return Math.PI;
      default: return -Math.PI / 2;
    }
  }

  private static getMountParts(
    mount: BarrierMount,
    _head: BarrierHead,
    mount_length: number
  ): BarrierPart[]
  {
    // Tiles are laid end to end by their MEASURED length, so they meet exactly
    // whatever MOUNT_ROD_TILE_LENGTH / MOUNT_ROD_THICKNESS are, and the first
    // one starts right at the pivot, under the bearing cap.
    const tile = {
      model: ResourceID.BarrierPillarModel,
      stretch: Barrier.MOUNT_ROD_TILE_LENGTH,
      thickness: Barrier.MOUNT_ROD_THICKNESS,
    };
    const tile_box = Barrier.measure( tile );
    const tile_length = tile_box.max.x - tile_box.min.x;
    const tiles = Math.max( 1, Math.ceil( mount_length / tile_length - 1e-6 ) );
    const angle = Barrier.headAngle( mount ) + Math.PI;
    const parts: BarrierPart[] = [];

    for ( let i = 0; i < tiles; i++ )
      parts.push( {
        ...tile,
        offset: new THREE.Vector3( i * tile_length - tile_box.min.x, 0, 0 ),
        angle,
        visual_layer: 0,
      } );

    return [
      ...parts,
      {
        model: ResourceID.BarrierBearingModel,
        offset: new THREE.Vector3(),
        angle: 0,
        visual_layer: 2,
        size: Barrier.BEARING_SIZE,
      },
    ];
  }

  private static getHeadParts( head: BarrierHead, mount: BarrierMount ): BarrierPart[]
  {
    if ( head === BarrierHead.Blade )
    {
      // Blades are fitted to the bearing they turn on. Each blade is authored
      // pointing +Y with its root some way out from the hub, tucked under the
      // cap by a certain share of the cap's radius. Keep that SHARE, measured
      // from the art at size 1, against the cap as it is actually sized — so a
      // smaller cap pulls the blades in and a bigger one pushes them out, and
      // they neither float clear of it nor sink into it. At default sizes the
      // shift is zero.
      const blade = { model: ResourceID.BarrierBladeModel, size: Barrier.BLADE_SIZE };
      const authored_tuck =
        Barrier.measure( { model: ResourceID.BarrierBladeModel } ).min.y / Barrier.bearingRadius( 1 );
      const root_y = Barrier.bearingRadius( Barrier.BEARING_SIZE ) * authored_tuck;
      const shift = root_y - Barrier.measure( blade ).min.y;
      const parts: BarrierPart[] = [];

      for ( let i = 0; i < Barrier.BLADE_COUNT; i++ )
        parts.push( {
          ...blade,
          // Along the blade's own length (+Y before `angle`).
          offset: new THREE.Vector3( 0, shift, 0 ),
          angle: ( i * 2 * Math.PI ) / Barrier.BLADE_COUNT,
          visual_layer: 1,
        } );

      return parts;
    }

    // The head goes on the MEASURED far end of the arm, so it stays on the tip
    // whatever HAMMER_ARM_LENGTH / HAMMER_ARM_THICKNESS are.
    const head_angle = Barrier.headAngle( mount );
    const arm_part = {
      model: ResourceID.BarrierPillarModel,
      stretch: Barrier.HAMMER_ARM_LENGTH,
      thickness: Barrier.HAMMER_ARM_THICKNESS,
    };
    const arm = Barrier.measure( arm_part ).max.x;

    return [
      {
        ...arm_part,
        offset: new THREE.Vector3(),
        angle: head_angle,
        visual_layer: 0,
      },
      {
        model: ResourceID.BarrierHammerModel,
        offset: new THREE.Vector3( 0, -arm, 0 ),
        angle: head_angle + Math.PI / 2,
        visual_layer: 1,
        size: Barrier.HAMMER_HEAD_SIZE,
      },
    ];
  }

  /**
   * Builds one part's visual and the matching collider on the body it belongs
   * to.
   *
   * A part is measured BEFORE its own turn is applied, so a blade or a hanging
   * rod is boxed along its own length instead of around the diagonal an
   * axis-aligned box would give it; the turn then goes onto the collider. One
   * box per part rather than one around the whole barrier — the blades and the
   * rod are thin, and a single box would swallow the gaps between them.
   */
  private addPart(
    part: BarrierPart,
    body: RAPIER.RigidBody,
    group: "mount" | "head",
    debug_parts: BarrierPartDebug[]
  ): THREE.Object3D
  {
    const visual = ResourceManager.getModel( part.model );
    const material = Barrier.getMaterial();

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      // These are foreground gameplay props. A ceiling-length support casts
      // an enormous off-screen shadow across the scene, so barriers do not
      // participate in the world's shadow map.
      child.castShadow = false;
      child.receiveShadow = false;
      child.material = material;
    } );

    Barrier.orientAndScale( visual, part );
    visual.position.copy( part.offset );
    visual.updateMatrixWorld( true );

    const box = new THREE.Box3().setFromObject( visual );
    const size = box.getSize( new THREE.Vector3() );
    const center = box.getCenter( new THREE.Vector3() );

    if ( size.x <= 0 || size.y <= 0 || size.z <= 0 )
      throw new Error( `Barrier part ${part.model} has invalid dimensions` );

    const cos = Math.cos( part.angle );
    const sin = Math.sin( part.angle );

    // These assets intentionally overlap at their joints, so the art goes in
    // an explicit back-to-front stack: the arm cannot cut across the hammer or
    // bearing, and the bearing cap covers the roots of the blades.
    const layer_depth = PhysicsCube.getSize().z * Barrier.VISUAL_LAYER_DEPTH_CELLS;
    const visual_z = part.visual_layer * layer_depth;

    // The collider rides along with its own piece: a ball bounces off a blade
    // where the blade is drawn, not half a cell behind it. Each slab is padded
    // out to a full layer step so neighbouring layers still meet in depth and
    // nothing can slip through the seam between them.
    const collider_depth = Math.max( size.z, layer_depth );

    PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc
        .cuboid( size.x * 0.5, size.y * 0.5, collider_depth * 0.5 )
        .setTranslation(
          center.x * cos - center.y * sin,
          center.x * sin + center.y * cos,
          center.z + visual_z
        )
        .setRotation( Barrier.quaternionAboutZ( part.angle ) )
        .setFriction( Barrier.FRICTION )
        .setRestitution( Barrier.RESTITUTION ),
      body
    );

    visual.position.z += visual_z;

    const round = ( value: number ): number => Number( value.toFixed( 4 ) );
    debug_parts.push( {
      group,
      model: ResourceID[part.model],
      angle_deg: round( THREE.MathUtils.radToDeg( part.angle ) ),
      stretch: round( part.stretch ?? 1 ),
      size: round( part.size ?? 1 ),
      thickness: round( part.thickness ?? 1 ),
      layer: part.visual_layer,
      visual_z: round( visual_z ),
      offset_x: round( part.offset.x ),
      offset_y: round( part.offset.y ),
      size_x: round( size.x ),
      size_y: round( size.y ),
      size_z: round( size.z ),
      collider_x: round( center.x * cos - center.y * sin ),
      collider_y: round( center.x * sin + center.y * cos ),
      collider_z: round( center.z + visual_z ),
      collider_depth: round( collider_depth ),
    } );

    const holder = new THREE.Group();
    holder.rotation.z = part.angle;
    holder.add( visual );

    return holder;
  }

  private static logAssembly(
    id: number,
    head: BarrierHead,
    mount: BarrierMount,
    bearing: THREE.Vector3,
    mount_length: number,
    parts: BarrierPartDebug[]
  ): void
  {
    const snapshot = {
      id,
      head: BarrierHead[head],
      mount: BarrierMount[mount],
      bearing: bearing.toArray().map( value => Number( value.toFixed( 4 ) ) ),
      mount_length: Number( mount_length.toFixed( 4 ) ),
      model_scale: Number( Barrier.getModelScale().toFixed( 6 ) ),
      parts,
    };

    const debug_window = window as typeof window & { __barrierDebug?: typeof snapshot[] };
    ( debug_window.__barrierDebug ??= [] ).push( snapshot );

    console.groupCollapsed( `[BarrierDebug #${id}] ${snapshot.head}/${snapshot.mount}` );
    console.info( "assembly", snapshot );
    console.table( parts );
    console.groupEnd();
  }

  /**
   * One tile of the mount rod exactly as the barriers draw it, but visual only:
   * no collider, lying along +X from x = 0. Returns the tile and its length, so
   * callers can lay tiles end to end the same way getMountParts does.
   */
  public static createRodTile(): { mesh: THREE.Object3D; length: number }
  {
    const part = {
      model: ResourceID.BarrierPillarModel,
      stretch: Barrier.MOUNT_ROD_TILE_LENGTH,
      thickness: Barrier.MOUNT_ROD_THICKNESS,
    };
    const visual = ResourceManager.getModel( part.model );
    const material = Barrier.getMaterial();

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.castShadow = false;
      child.receiveShadow = false;
      child.material = material;
    } );

    Barrier.orientAndScale( visual, part );
    visual.updateMatrixWorld( true );

    const box = new THREE.Box3().setFromObject( visual );
    visual.position.x = -box.min.x;

    const mesh = new THREE.Group();
    mesh.add( visual );

    return { mesh, length: box.max.x - box.min.x };
  }

  private static getMaterial(): THREE.MeshLambertMaterial
  {
    if ( !Barrier.material )
    {
      const base_map = ResourceManager.getTexture( ResourceID.BarrierTexture );
      Barrier.material = new THREE.MeshLambertMaterial( {
        map: base_map,
        normalMap: ResourceManager.getNormalMap( ResourceID.BarrierNormal ),
        normalScale: new THREE.Vector2( 0.6, 0.6 ),
        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: base_map,
        emissiveIntensity: 0.12,
        side: THREE.FrontSide,
      } );
    }

    return Barrier.material;
  }

  private static quaternionAboutZ( angle: number ): RAPIER.Rotation
  {
    return { x: 0, y: 0, z: Math.sin( angle * 0.5 ), w: Math.cos( angle * 0.5 ) };
  }

  public getBodyHandles(): number[]
  {
    return [ this.mount_body.handle, this.head_body.handle ];
  }

  public update( dt: number ): void
  {
    if ( this.disposed )
      return;

    this.time += dt;

    this.angle = this.head === BarrierHead.Blade
      ? this.rest_angle + this.time * Barrier.BLADE_TURNS_PER_SEC * Math.PI * 2
      : this.rest_angle + Barrier.HAMMER_SWING_AMPLITUDE
        * Math.sin( ( this.time / Barrier.HAMMER_SWING_PERIOD ) * Math.PI * 2 );

    // setNextKinematicRotation, not setRotation: it hands Rapier the motion
    // between this step and the next, so a blade actually sweeps the ball
    // along instead of teleporting past it.
    this.head_body.setNextKinematicRotation( Barrier.quaternionAboutZ( this.angle ) );
    this.head_mesh.rotation.z = this.angle;
  }

  public dispose(): void
  {
    if ( this.disposed )
      return;

    this.disposed = true;

    SceneManager.world.remove( this.mount_mesh );
    SceneManager.world.remove( this.head_mesh );

    const world = PhysicsWorld.getWorld();
    try { world.removeRigidBody( this.mount_body ); } catch {}
    try { world.removeRigidBody( this.head_body ); } catch {}
  }
}
